<script setup>
import { Head, Link } from '@inertiajs/vue3'
import {
  ShieldCheck, GraduationCap, CalendarCheck, MessageSquare, Receipt,
  CheckCircle2, ChevronRight,
} from 'lucide-vue-next'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageHero from '@/Components/Marketing/PageHero.vue'
import { Button } from '@/Components/ui/button'
import { tutors, faq, subjects as subjectsRoute } from '@/routes/index'
import { subjectIcon } from '@/lib/subjectIcon'
import { formatMoney } from '@/lib/money'

/**
 * The page for the person who pays.
 *
 * It answers, in order, the four things a parent asks: who is teaching my child,
 * is it safe, what can I see, and what does it cost. Every figure comes from the
 * database — there are no invented outcome statistics on this page, because we
 * cannot yet evidence any.
 */
const props = defineProps({
  figures: { type: Object, default: () => ({}) },
  subjects: { type: Array, default: () => [] },
})

const assurances = [
  {
    icon: GraduationCap,
    title: 'Graded, not just qualified',
    body: 'We verify each tutor’s university, their degree and the A-level grades they achieved themselves. Someone who cannot evidence their own results does not teach on Bindle.',
  },
  {
    icon: ShieldCheck,
    title: 'DBS checked before they teach',
    body: 'A current DBS check is a condition of being listed, and we check it again as part of approval. No exceptions, including for tutors who came recommended.',
  },
  {
    icon: MessageSquare,
    title: 'Every conversation on the platform',
    body: 'Messages between your child and their tutor happen inside Bindle, and the account holder can read them. Nothing moves to a private number or a personal email.',
  },
]

const control = [
  { icon: CalendarCheck, title: 'The schedule', body: 'Every lesson, past and upcoming, on one calendar you can subscribe to from your own phone.' },
  // Progress tracking against a target grade is not built. This says what the
  // account actually gives a parent today: a way to reach the tutor.
  { icon: MessageSquare, title: 'Tutor support', body: 'Message the tutor between lessons and read everything your child has said to them. You are in the conversation without having to sit in the room.' },
  { icon: Receipt, title: 'Money', body: 'Every payment produces a downloadable invoice. Cancel from your account in one step, with no notice period.' },
]

const steps = [
  { n: '01', title: 'Shortlist a tutor', body: 'Filter by subject, level and exam board. Every profile shows where they studied, what they scored and what they charge per week.' },
  { n: '02', title: 'Message before you commit', body: 'Ask about approach, availability and what your child needs. There is no obligation and no card required to start a conversation.' },
  { n: '03', title: 'Subscribe weekly', body: 'Agree a time, subscribe at the tutor’s price, and lessons begin. Each lesson is paid for 48 hours before it, with no joining fee and no minimum term.' },
  { n: '04', title: 'Stay in the loop', body: 'Follow the schedule, read the feedback, and change or cancel whenever you need to.' },
]
</script>

<template>
  <Head>
    <title>For parents — Bindle</title>
    <meta name="description" content="How Bindle selects tutors, keeps lessons safe, and gives parents visibility of schedule, progress and payments." />
  </Head>

  <AppLayout>
    <PageHero
      eyebrow="For parents"
      title="You are trusting someone with your child’s grades. Here is how we earn it."
      lead="Bindle exists because it is almost impossible to tell, from a marketplace profile, whether a tutor has actually done what your child is trying to do. We check that before anyone is listed, and we show you the evidence."
    >
      <!--
        One button, because there was only ever one destination.

        "See tutors and their prices" sat beside this and pointed at the same
        listing — two buttons offering a parent a choice that did not exist. The
        prices are on that listing, where each card shows the price of a lesson and
        the results can be sorted by it, so nothing is lost by not saying so
        twice. This is also the wording every other page uses.
      -->
      <template #actions>
        <Link :href="tutors()"><Button size="lg">Find your tutor</Button></Link>
      </template>
    </PageHero>

    <!-- ==================== ASSURANCE ==================== -->
    <section class="section">
      <div class="container-page">
        <h2 class="max-w-2xl text-2xl md:text-3xl">What we check before a tutor can teach</h2>
        <div class="mt-8 grid gap-5 md:grid-cols-3">
          <article
            v-for="(item, index) in assurances"
            :key="item.title"
            v-reveal="{ delay: index * 80 }"
            class="surface-card p-6"
          >
            <span class="grid size-12 place-items-center rounded-full bg-brand-soft text-brand">
              <component :is="item.icon" class="size-6" />
            </span>
            <h3 class="mt-5 text-xl">{{ item.title }}</h3>
            <p class="mt-2.5 text-[15px] leading-relaxed text-muted">{{ item.body }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- ==================== HOW IT WORKS ==================== -->
    <section class="border-y border-line bg-surface-2 py-14 md:py-20">
      <div class="container-page">
        <h2 class="max-w-2xl text-2xl md:text-3xl">From shortlist to first lesson</h2>
        <ol class="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <li
            v-for="(step, index) in steps"
            :key="step.n"
            v-reveal="{ delay: index * 70 }"
            class="rounded-xl border border-line bg-surface p-6"
          >
            <span class="font-display text-3xl font-bold text-brand/35">{{ step.n }}</span>
            <h3 class="mt-3 text-lg">{{ step.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-muted">{{ step.body }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ==================== VISIBILITY ==================== -->
    <section class="section">
      <div class="container-page grid gap-10 lg:grid-cols-2 lg:items-center">
        <div>
          <h2 class="text-2xl md:text-3xl">You can see everything, without sitting in</h2>
          <p class="mt-3 max-w-xl text-[15px] leading-relaxed text-muted">
            A teenager will not thank you for watching their lesson. The account gives you the same
            information without being in the room.
          </p>
          <ul class="mt-7 space-y-5">
            <li v-for="item in control" :key="item.title" class="flex gap-4">
              <span class="grid size-10 shrink-0 place-items-center rounded-full bg-brand-soft text-brand">
                <component :is="item.icon" class="size-5" />
              </span>
              <div>
                <h3 class="text-base">{{ item.title }}</h3>
                <p class="mt-1 text-sm leading-relaxed text-muted">{{ item.body }}</p>
              </div>
            </li>
          </ul>
        </div>

        <div v-reveal class="rounded-xl bg-brand p-8 text-brand-contrast">
          <p class="text-sm font-semibold uppercase tracking-[0.14em] opacity-80">Right now on Bindle</p>
          <dl class="mt-6 space-y-6">
            <div>
              <dt class="text-sm opacity-85">Tutors taking students</dt>
              <dd class="font-display text-4xl font-bold leading-none">{{ figures.tutor_count ?? '—' }}</dd>
            </div>
            <div class="border-t border-white/25 pt-6">
              <dt class="text-sm opacity-85">Subjects covered</dt>
              <dd class="font-display text-4xl font-bold leading-none">{{ figures.subject_count ?? '—' }}</dd>
            </div>
            <div v-if="figures.one_to_one_from" class="border-t border-white/25 pt-6">
              <dt class="text-sm opacity-85">One-to-one lessons from</dt>
              <dd class="font-display text-4xl font-bold leading-none">
                {{ formatMoney(figures.one_to_one_from) }}<span class="text-lg font-normal">/week</span>
              </dd>
            </div>
          </dl>
          <p class="mt-7 text-xs leading-relaxed opacity-75">
            Counted from the platform when this page loaded, not from a brochure.
          </p>
        </div>
      </div>
    </section>

    <!-- ==================== SUBJECTS ==================== -->
    <section v-if="subjects.length" class="border-t border-line bg-surface-2 py-14 md:py-20">
      <div class="container-page">
        <div class="flex flex-wrap items-end justify-between gap-4">
          <h2 class="text-2xl md:text-3xl">Subjects your child can start today</h2>
          <Link :href="subjectsRoute()" class="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
            All subjects <ChevronRight class="size-4" />
          </Link>
        </div>
        <ul class="mt-7 flex flex-wrap gap-2.5">
          <li v-for="subject in subjects" :key="subject.slug">
            <Link
              :href="tutors.url({ query: { subject: subject.slug } })"
              class="inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-2 text-sm font-semibold text-ink transition-colors hover:border-line-brand hover:bg-brand-soft hover:text-brand-soft-text"
            >
              <span class="material-symbols-outlined text-base" aria-hidden="true">{{ subjectIcon(subject) }}</span>
              {{ subject.name }}
            </Link>
          </li>
        </ul>
      </div>
    </section>

    <!-- ==================== CLOSING ==================== -->
    <section class="section">
      <div class="container-page">
        <div class="panel-brand flex flex-col gap-6 p-8 md:flex-row md:items-center md:justify-between md:p-10">
          <div class="max-w-xl">
            <h2 class="text-2xl">Still deciding?</h2>
            <p class="mt-2 text-[15px] leading-relaxed">
              Message a tutor before you spend anything. Ask what they would do in your child’s
              situation — the answer tells you more than any profile.
            </p>
            <ul class="mt-5 space-y-2 text-sm">
              <li class="flex items-center gap-2"><CheckCircle2 class="size-4 shrink-0" /> No card needed to start a conversation</li>
              <li class="flex items-center gap-2"><CheckCircle2 class="size-4 shrink-0" /> No minimum term, cancel in one step</li>
            </ul>
          </div>
          <div class="flex shrink-0 flex-col gap-3">
            <Link :href="tutors()"><Button size="lg" class="w-full">Browse tutors</Button></Link>
            <Link :href="faq()"><Button variant="quiet" size="lg" class="w-full">Read the FAQ</Button></Link>
          </div>
        </div>
      </div>
    </section>
  </AppLayout>
</template>
